import { datadogRum } from '@datadog/browser-rum';
import deepmerge from 'deepmerge';
import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import type React from 'react';
import type { TSectionId } from '~/constants/vdp/section';
import type { DeepPartial } from '~/types';

export type ActionsContextType = {
  markSectionReady: (sectionId: TSectionId) => void;
  registerSection: (sectionId: TSectionId) => void;
};

export type StateContextType = {
  readyStates: Record<string, boolean>;
  registeredSections: Set<string>;
};

export type ContextType = ActionsContextType & StateContextType;

const defaultActions: ActionsContextType = {
  markSectionReady() {},
  registerSection() {}
};

const defaultState: StateContextType = {
  readyStates: {},
  registeredSections: new Set()
};

const defaultContext: ContextType = { ...defaultActions, ...defaultState };

// DD actions: vdp-section-{registered,ready}
export const SectionReadinessActionsContext =
  createContext<ActionsContextType>(defaultActions);
export const SectionReadinessStateContext =
  createContext<StateContextType>(defaultState);

export function SectionReadinessProvider({
  children
}: React.PropsWithChildren) {
  const readySectionsRef = useRef<Set<string>>(new Set());
  const readyStatesRef = useRef<Record<string, boolean>>({});
  const registeredSectionsRef = useRef<Set<string>>(new Set());
  const [stateVersion, setStateVersion] = useState(0);

  const registerSection = useCallback((sectionId: TSectionId) => {
    if (registeredSectionsRef.current.has(sectionId)) return;

    registeredSectionsRef.current.add(sectionId);
    datadogRum.addAction('vdp-section-registered', {
      sectionId,
      timestamp: performance.now()
    });
  }, []);

  const markSectionReady = useCallback((sectionId: TSectionId) => {
    if (readySectionsRef.current.has(sectionId)) return;

    readySectionsRef.current.add(sectionId);
    readyStatesRef.current = {
      ...readyStatesRef.current,
      [sectionId]: true
    };
    datadogRum.addAction('vdp-section-ready', {
      sectionId,
      timestamp: performance.now()
    });
    setStateVersion(v => v + 1);
  }, []);

  const actions = useMemo<ActionsContextType>(
    () => ({ markSectionReady, registerSection }),
    [markSectionReady, registerSection]
  );

  const state = useMemo<StateContextType>(
    () => ({
      readyStates: readyStatesRef.current,
      registeredSections: registeredSectionsRef.current
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateVersion triggers re-computation; refs provide current values
    [stateVersion]
  );

  return (
    <SectionReadinessActionsContext.Provider value={actions}>
      <SectionReadinessStateContext.Provider value={state}>
        {children}
      </SectionReadinessStateContext.Provider>
    </SectionReadinessActionsContext.Provider>
  );
}

export const generateSectionReadinessContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);
